import type { HsGraphics } from '@lib/core-ts/graphic-render-new/graphics.class';
import type { HsMaterial } from '@lib/core-ts/graphic-render-new/materials/material.class';
import { HsGraphicCollection } from '@lib/core-ts/graphic-render-new/objects/graphic-collection.class';
import type { HsGraphicObject } from '@lib/core-ts/graphic-render-new/objects/graphic-object.class';

/** What the tint reads of the drawn scene. */
export interface NodeTintScene {
  /** Drawn meshes by render key; instanced keys map to their collection. */
  meshes(): ReadonlyMap<string, HsGraphicObject>;
  /** Render keys of a collection's instances, in instance order. */
  collectionRenderKeys(collection: HsGraphicCollection): readonly string[] | undefined;
  nodeIdOf(renderKey: string): string | null;
  graphics(): HsGraphics | null;
  /** A mesh material in `rgba`, built with the scene's look so clipping and lighting match; null without a context. */
  material(rgba: readonly [number, number, number, number]): HsMaterial | null;
}

/** Nodes drawn in one colour. */
export interface NodeTintGroup {
  nodeIds: ReadonlySet<string>;
  rgba: readonly [number, number, number, number];
}

/** Colour of every node outside the groups; instanced meshes take the colour without alpha. */
export interface NodeTintGhost {
  rgb: readonly [number, number, number];
  alpha: number;
}

/**
 * Colours scene nodes in several colours at once and optionally ghosts the rest; the stand's selection
 * highlight is one group of it. Single meshes swap to a material of the colour (with real transparency for the
 * ghost) and instances of instanced meshes take per-instance colours. Scene refreshes replace objects and buffers,
 * so the host applies the tint again after each one.
 */
export class NodeTint {
  /** Swapped meshes with their own material and the one the tint gave them. */
  private readonly swapped: Map<HsGraphicObject, { original: HsMaterial; applied: HsMaterial }> = new Map();
  private readonly tinted: Set<HsGraphicCollection> = new Set();

  public constructor(private readonly scene: NodeTintScene) {}

  public get active(): boolean {
    return this.swapped.size > 0 || this.tinted.size > 0;
  }

  /** Replaces the previous tint; later groups win for nodes in several. Returns the number of tinted meshes. */
  public apply(groups: readonly NodeTintGroup[], ghost: NodeTintGhost | null): number {
    this.clear();
    const graphics: HsGraphics | null = this.scene.graphics();
    if (graphics === null || (groups.length === 0 && ghost === null)) {
      return 0;
    }
    const colorByNode: Map<string, readonly [number, number, number, number]> = new Map();
    for (const group of groups) {
      group.nodeIds.forEach((nodeId: string) => colorByNode.set(nodeId, group.rgba));
    }
    const ghostRgba: readonly [number, number, number, number] | null =
      ghost === null ? null : [ghost.rgb[0], ghost.rgb[1], ghost.rgb[2], ghost.alpha];
    const materials: Map<string, HsMaterial | null> = new Map();
    const materialOf = (rgba: readonly [number, number, number, number]): HsMaterial | null => {
      const key: string = rgba.join('|');
      if (!materials.has(key)) {
        materials.set(key, this.scene.material(rgba));
      }
      return materials.get(key) ?? null;
    };
    let count: number = 0;
    const instancesByCollection: Map<HsGraphicCollection, Map<number, readonly number[]>> = new Map();
    for (const [renderKey, object] of this.scene.meshes()) {
      const nodeId: string | null = this.scene.nodeIdOf(renderKey);
      const rgba: readonly [number, number, number, number] | null =
        (nodeId === null ? undefined : colorByNode.get(nodeId)) ?? ghostRgba;
      if (rgba === null) {
        continue;
      }
      if (object instanceof HsGraphicCollection) {
        const index: number = this.scene.collectionRenderKeys(object)?.indexOf(renderKey) ?? -1;
        if (index >= 0) {
          const instances: Map<number, readonly number[]> = instancesByCollection.get(object) ?? new Map();
          instances.set(index, [rgba[0], rgba[1], rgba[2]]);
          instancesByCollection.set(object, instances);
          count++;
        }
      } else if (!this.swapped.has(object)) {
        const material: HsMaterial | null = materialOf(rgba);
        if (material !== null) {
          this.swapped.set(object, { original: object.material, applied: material });
          object.material = material;
          count++;
        }
      }
    }
    instancesByCollection.forEach((instances: Map<number, readonly number[]>, collection: HsGraphicCollection) => {
      collection.setInstanceColorOverrides(instances, graphics);
      this.tinted.add(collection);
    });
    return count;
  }

  /** Restores the materials and instance colours the tint replaced. */
  public clear(): void {
    this.swapped.forEach((entry: { original: HsMaterial; applied: HsMaterial }, object: HsGraphicObject) => {
      // A refresh may have given the object a new material since; keep that one.
      if (object.material === entry.applied && !object.isDeleted) {
        object.material = entry.original;
      }
    });
    this.swapped.clear();
    const graphics: HsGraphics | null = this.scene.graphics();
    if (graphics !== null) {
      this.tinted.forEach((collection: HsGraphicCollection) => {
        if (!collection.isDeleted) {
          collection.setInstanceColorOverrides(new Map(), graphics);
        }
      });
    }
    this.tinted.clear();
  }
}
